import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import feedbackInstanceService, {
  CreateFeedbackInstanceDto,
  UpdateFeedbackInstanceDto,
  FeedbackInstance,
  EmailTemplate,
} from '../services/feedbackInstanceService';

export const useFeedbackInstances = () => {
  return useQuery({
    queryKey: ['feedback-instances', 'list'],
    queryFn: () => feedbackInstanceService.getAllInstances(),
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useFeedbackInstancesByFeedbackId = (feedbackId: string) => {
  return useQuery({
    queryKey: ['feedback-instances', 'by-feedback', feedbackId],
    queryFn: () => feedbackInstanceService.getInstancesByFeedbackId(feedbackId),
    enabled: !!feedbackId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedbackInstance = (id: string) => {
  return useQuery({
    queryKey: ['feedback-instances', 'detail', id],
    queryFn: () => feedbackInstanceService.getInstanceById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMyFeedback = () => {
  return useQuery({
    queryKey: ['feedback-instances', 'my-feedback'],
    queryFn: () => feedbackInstanceService.getMyFeedback(),
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useCreateFeedbackInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFeedbackInstanceDto) => feedbackInstanceService.createInstance(data),
    onSuccess: async (instance: FeedbackInstance) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'my-feedback'] });
      if (instance?.feedbackId) {
        await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'by-feedback', instance.feedbackId] });
      }
    },
  });
};

export const useUpdateFeedbackInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeedbackInstanceDto }) =>
      feedbackInstanceService.updateInstance(id, data),
    onSuccess: async (updatedInstance) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'my-feedback'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'detail', updatedInstance.id] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'by-feedback', updatedInstance.feedbackId] });
    },
  });
};

export const useDeleteFeedbackInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => feedbackInstanceService.deleteInstance(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances'] });
    },
  });
};

export const useSendInvitations = () => {
  return useMutation({
    mutationFn: ({ instanceId, subject, body }: { instanceId: string; subject?: string; body?: string }) =>
      feedbackInstanceService.sendInvitations(instanceId, subject && body ? { subject, body } : undefined),
  });
};

export const useSaveEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instanceId, template }: { instanceId: string; template: EmailTemplate }) =>
      feedbackInstanceService.saveEmailTemplate(instanceId, template),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'detail', variables.instanceId] });
    },
  });
};

export const useSendReminders = () => {
  return useMutation({
    mutationFn: ({ instanceId, subject, body }: { instanceId: string; subject?: string; body?: string }) =>
      feedbackInstanceService.sendReminders(instanceId, subject && body ? { subject, body } : undefined),
  });
};

export const useEmailStatus = (instanceId: string | undefined, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['email-status', instanceId],
    queryFn: () => feedbackInstanceService.getEmailStatus(instanceId!),
    enabled: !!instanceId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 3000;
    },
  });
};
